import { scaleAnimationDurations } from '@/_pure/animationDurations';
import { BASE_CONFIG } from '@/workflow/_data/config/config';
import { WORKFLOW_KEYS } from '@/workflow/_pure/keyCodec';
import { PRESENTATION_DEFAULTS } from '@/workflow-runner/_data/config/fragments/_@shared/presentationDefaults';
import { CAMERA_DEFAULTS } from '@/workflow-runner/_data/config/fragments/camera';
import type { AuthoredModeConfig, Config } from '@/workflow-runner/_contract/config';
import { buildRunnerModeConfig } from '@/workflow-runner/_pure/config/presentation';

export const AUTHORED_MODE_CONFIG: AuthoredModeConfig = {
    showOperationCaption: true,
    traceSource: 'remote',
    camera: CAMERA_DEFAULTS,
    workflowStateEntries: {
        operationPresentationMode: 'panels',
    },
    workflowStateEntryConnectors: {
        hideInWheelsMode: false,
        hideForTransformedBooleanOperations: false,
        hideIncomingForTransformedBooleanOperations: true,
    },
    standoffNearFraction: 0.2,
    standoffFarFraction: 0.4,
    traceSelection: {
        workflowTraceSelectionKind: 'latest-by-timestamp',
        replayPositionIndex: 0,
    },
    playback: {
        inputPotentialPropagationDurationMs: 250,
        positionIntervalMs: 300,
        startPlaying: false,
        resourceTypeRadialActivation: {
            inputDurationMs: 600,
            processingHoldMs: 180,
            postOutputHoldMs: 250,
            outputDurationMs: 600,
        },
        resourceTypeRadialRipple: {
            enabled: true,
            amplitude: 1.60,
            pulseLength: 3,
            oscillationCount: 2,
        },
        loopContinuationAnimation: {
            transitionDurationMs: 500,
            spaceShiftPhaseEnd: 0.6,
            eccentricLead: {
                enabled: false,
                leadFactor: 1.0,
                splitFactor: 0.45,
                colorPulse: true,
            },
        },
        branchExitRebindAnimation: {
            transitionDurationMs: 300,
            spaceShiftPhaseEnd: 0.4,
            eccentricLead: {
                enabled: false,
                leadFactor: 1.0,
                splitFactor: 0.45,
                colorPulse: true,
            },
        },
        naturalLessThanTransport: {
            naturalRadialEntryDurationMs: 100,
            naturalRingHingeDurationMs: 520,
            toolStepRingTravelDurationMs: 520,
            booleanRingHingeDurationMs: 520,
            booleanRadialExitDurationMs: 100,
            settlementDurationMs: 0,
        },
        booleanGraphTransformation: {
            unfoldWheelMovementDurationMs: 520,
            unfoldProfileOpeningDurationMs: 300,
            refoldProfileClosingDurationMs: 300,
            refoldWheelMovementDurationMs: 520,
        },
    },
    nonActiveDimming: {
        enabledByConstellationKey: {
            [WORKFLOW_KEYS.constellations.tool]: true,
        },
        defaultOpacity: 0.2,
    },
};

export const UNSCALED_MODE_CONFIG = buildRunnerModeConfig(AUTHORED_MODE_CONFIG);

export const MODE_CONFIG = scaleAnimationDurations(
    UNSCALED_MODE_CONFIG,
    PRESENTATION_DEFAULTS.animationDurationMultiplier,
);

const scaledPresentationDefaults = scaleAnimationDurations<{
    cameraTransitionDurationMs: number;
    minimumPositionIntervalMs: number;
}>(
    PRESENTATION_DEFAULTS,
    PRESENTATION_DEFAULTS.animationDurationMultiplier,
);
export const CAMERA_TRANSITION_DURATION_MS = scaledPresentationDefaults.cameraTransitionDurationMs;
export const MINIMUM_POSITION_INTERVAL_MS = scaledPresentationDefaults.minimumPositionIntervalMs;

export const CONFIG: Config = {
    ...BASE_CONFIG,
    adapterMode: 'runner',
    adapterModeConfig: MODE_CONFIG,
};